import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'next-i18next';
import { Range } from 'react-range';
import Aside from '@components/Aside/Aside';
import CategoriesFilterPanel from '@features/categories/components/CategoriesFilterPanel/CategoriesFilterPanel';
import styles from '@components/InteriorDesignsFilters/InteriorDesignsFilters.styles';
import rangeStyles from '@features/categories/components/CategoriesFilterRange/CategoriesFilterRange.styles';
import CategoriesFilterRangeTrack from '@features/categories/components/CategoriesFilterRange/components/CategoriesFilterRangeTrack';
import CategoriesFilterRangeThumb from '@features/categories/components/CategoriesFilterRange/components/CategoriesFilterRangeThumb';
import ErrorBoundary from '@components/errors/ErrorBoundary';
import Heading from '@components/Heading/Heading';
import Icon from '@components/icons/Icon';
import Button from '@components/Button/Button';
import ArrowBack from '@components/icons/components/ArrowBack';
import { IInteriorDesignFilter, IRenderTrackParams, IRenderThumbParams } from '@features/cms/typescript';
import { useResponsive } from '@features/responsive';
import Add from '@components/icons/components/Add';
import Checkbox from '@components/Checkbox/Checkbox';
type PropsType = {
  asideActive: boolean;
  setShowFilters: (showFilters: boolean) => void;
  filters: IInteriorDesignFilter[] | null;
  setFiltersState: (filters: IInteriorDesignFilter[]) => void;
};
const InteriorDesignsFiltersAside = ({
  asideActive,
  filters,
  setShowFilters,
  setFiltersState
}: PropsType) => {
  const {
    t
  } = useTranslation('filters');
  const {
    t: tCommon
  } = useTranslation('common');
  const {
    isMobile
  } = useResponsive();
  const [activeTypeFilters, setActiveTypeFilters] = useState<IInteriorDesignFilter['type'] | null>(null);
  const [surfaceRange, setSurfaceRange] = useState<{
    min: number;
    max: number;
  }>({
    min: 0,
    max: 0
  });
  const [surfaceCurrentRange, setSurfaceCurrentRange] = useState<{
    min: number;
    max: number;
  }>({
    min: 0,
    max: 0
  });
  const [surfaceLocalValue, setSurfaceLocalValue] = useState<{
    min: string;
    max: string;
  }>({
    min: '',
    max: ''
  });
  const [surfaceSliderValue, setSurfaceSliderValue] = useState<number[]>([0, 0]);
  const [errorMinValue, setErrorMinValue] = useState(false);
  const [errorMaxValue, setErrorMaxValue] = useState(false);
  const isInitializedRef = useRef(false);
  const [localeFilters, setLocaleFilters] = useState<IInteriorDesignFilter[] | null>(filters);
  const localeFiltersRef = useRef(localeFilters);
  localeFiltersRef.current = localeFilters;
  let setTimeoutJob: NodeJS.Timeout | null = null;
  useEffect(() => {
    setLocaleFilters([...(filters || [])]);
  }, [asideActive, filters]);

  // Initialize surface range only once when filters are first loaded
  useEffect(() => {
    if (localeFilters) {
      let restartRange = false;
      const surfaceFilters = localeFilters.filter(filter => filter.type === 'surface');
      if (surfaceFilters.length > 0) {
        const values = surfaceFilters.map(filter => parseInt(filter.value, 10)).filter(v => !isNaN(v));
        if (values.length > 0) {
          const min = Math.min(...values);
          const max = Math.max(...values);
          setSurfaceRange({
            min,
            max
          });
          const currentValues = surfaceFilters.filter(filter => filter.isChecked).map(filter => parseInt(filter.value, 10)).filter(v => !isNaN(v));
          // Check if there are any checked values, otherwise use the full range
          const minCurrent = currentValues.length > 0 ? Math.min(...currentValues) : min;
          const maxCurrent = currentValues.length > 0 ? Math.max(...currentValues) : max;
          setSurfaceCurrentRange({
            min: minCurrent,
            max: maxCurrent
          });
          setSurfaceLocalValue({
            min: minCurrent.toString(),
            max: maxCurrent.toString()
          });
          setSurfaceSliderValue([minCurrent, maxCurrent]);
          isInitializedRef.current = true;
        } else {
          restartRange = true;
        }
      } else {
        restartRange = true;
      }
      if (restartRange) {
        setSurfaceCurrentRange({
          ...surfaceRange
        });
        setSurfaceLocalValue({
          min: surfaceRange.min.toString(),
          max: surfaceRange.max.toString()
        });
        setSurfaceSliderValue([surfaceRange.min, surfaceRange.max]);
      }
    }
  }, [filters, filters?.filter(filter => filter.isChecked).length]);

  // Sync slider value with current range
  useEffect(() => {
    setSurfaceSliderValue([surfaceCurrentRange.min, surfaceCurrentRange.max]);
  }, [surfaceCurrentRange]);
  useEffect(() => {
    return () => {
      if (setTimeoutJob) {
        clearTimeout(setTimeoutJob);
      }
    };
  }, []);
  const renderFiltersTypeLink = (type: IInteriorDesignFilter['type']) => {
    return <Button variant="link" size="small" fullWidth letterCase="uppercase" onClick={() => setActiveTypeFilters(type)} addSx={styles.filterLinkButton}>
        {tCommon(type.toLowerCase())}
        <Icon size="18px">
          <Add />
        </Icon>
      </Button>;
  };
  const handleFilterClick = (filter: IInteriorDesignFilter) => {
    if (!localeFilters) return;
    const findIndex = localeFilters.findIndex(f => f.type === filter.type && f.value === filter.value);
    if (findIndex !== -1) {
      const newFiltersState = [...localeFilters];
      newFiltersState[findIndex].isChecked = !filter.isChecked;
      setLocaleFilters(newFiltersState);
    }
  };
  const renderFilterLink = (filter: IInteriorDesignFilter) => <div>
      <Checkbox value={filter.isChecked} onChange={() => handleFilterClick(filter)} showError={false} cy="CategoriesFilterMultiselectItem">
        {filter.value}
      </Checkbox>
    </div>;
  const handleChangeMinValue = (e: React.ChangeEvent<HTMLInputElement>, maxValue: string) => {
    const value = e.target.value.trim();
    setSurfaceLocalValue({
      ...surfaceLocalValue,
      min: value
    });
    if (value !== '' && (+value >= +maxValue || +value < surfaceRange.min)) {
      setErrorMinValue(true);
    } else if (errorMinValue) {
      setErrorMinValue(false);
    }
  };
  const handleChangeMaxValue = (e: React.ChangeEvent<HTMLInputElement>, minValue: string) => {
    const value = e.target.value.trim();
    setSurfaceLocalValue({
      ...surfaceLocalValue,
      max: value
    });
    if (value !== '' && (+value <= +minValue || +value > surfaceRange.max)) {
      setErrorMaxValue(true);
    } else if (errorMaxValue) {
      setErrorMaxValue(false);
    }
  };
  const handleSurfaceMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && value !== '') {
      const newRange = {
        min: Math.max(surfaceRange.min, Math.min(surfaceRange.max, numValue)),
        max: surfaceCurrentRange.max
      };
      setSurfaceLocalValue({
        ...surfaceLocalValue,
        min: newRange.min.toString()
      });
      setSurfaceCurrentRange(newRange);
      setSurfaceSliderValue([newRange.min, newRange.max]);
      setErrorMinValue(false);
      // Update filters based on range
      if (localeFilters) {
        const newFiltersState = localeFilters.map(filter => {
          if (filter.type === 'surface') {
            const filterValue = parseInt(filter.value, 10);
            if (!isNaN(filterValue)) {
              return {
                ...filter,
                isChecked: filterValue >= newRange.min && filterValue <= newRange.max
              };
            }
          }
          return filter;
        });
        setLocaleFilters(newFiltersState);
      }
    }
  };
  const handleSurfaceMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && value !== '') {
      const newRange = {
        min: surfaceCurrentRange.min,
        max: Math.max(surfaceRange.min, Math.min(surfaceRange.max, numValue))
      };
      setSurfaceLocalValue({
        ...surfaceLocalValue,
        max: newRange.max.toString()
      });
      setSurfaceCurrentRange(newRange);
      setSurfaceSliderValue([newRange.min, newRange.max]);
      setErrorMaxValue(false);
      // Update filters based on range
      if (localeFilters) {
        const newFiltersState = localeFilters.map(filter => {
          if (filter.type === 'surface') {
            const filterValue = parseInt(filter.value, 10);
            if (!isNaN(filterValue)) {
              return {
                ...filter,
                isChecked: filterValue >= newRange.min && filterValue <= newRange.max
              };
            }
          }
          return filter;
        });
        setLocaleFilters(newFiltersState);
      }
    }
  };
  const handleSurfaceSliderChange = (values: number[]) => {
    const [min, max] = values;
    const newRange = {
      min: Math.max(surfaceRange.min, Math.min(surfaceRange.max, min)),
      max: Math.max(surfaceRange.min, Math.min(surfaceRange.max, max))
    };
    setSurfaceSliderValue([newRange.min, newRange.max]);
    setSurfaceCurrentRange(newRange);
    setSurfaceLocalValue({
      min: newRange.min.toString(),
      max: newRange.max.toString()
    });
    // Update filters based on range
    if (localeFilters) {
      const newFiltersState = localeFilters.map(filter => {
        if (filter.type === 'surface') {
          const filterValue = parseInt(filter.value, 10);
          if (!isNaN(filterValue)) {
            return {
              ...filter,
              isChecked: filterValue >= newRange.min && filterValue <= newRange.max
            };
          }
        }
        return filter;
      });
      setLocaleFilters(newFiltersState);
    }
  };
  const renderSurfaceTrack = ({
    props,
    children
  }: IRenderTrackParams) => {
    // Create a mock filter object for the track component
    const mockFilter = {
      defaultValue: surfaceRange
    } as {
      defaultValue: {
        min: number;
        max: number;
      };
    };
    return <CategoriesFilterRangeTrack ref={props.ref as React.Ref<HTMLDivElement>} props={props as Record<string, unknown>} filter={mockFilter as never} value={surfaceSliderValue}>
        {children}
      </CategoriesFilterRangeTrack>;
  };
  const renderSurfaceThumb = ({
    props,
    isDragged,
    index
  }: IRenderThumbParams) => <CategoriesFilterRangeThumb key={index} props={props as Record<string, unknown>} isDragged={isDragged} />;
  const renderFilterTypeSurface = useMemo(() => {
    if (activeTypeFilters && activeTypeFilters !== 'surface') return null;
    if (surfaceRange.min === 0 && surfaceRange.max === 0) return null;
    const shouldRenderSlider = surfaceRange.min !== surfaceRange.max;
    return <div sx={rangeStyles.filterContainer(false)} data-cy="InteriorDesignsSurfaceRange">
        <div sx={rangeStyles.inputContainer}>
          <input sx={rangeStyles.numberInput(errorMinValue)} type="number" min={surfaceRange.min} max={surfaceRange.max} value={surfaceLocalValue.min} onChange={e => handleChangeMinValue(e, surfaceLocalValue.max)} onBlur={handleSurfaceMinChange} maxLength={6} />
          <span sx={rangeStyles.dash} />
          <input sx={rangeStyles.numberInput(errorMaxValue)} type="number" min={surfaceRange.min} max={surfaceRange.max} value={surfaceLocalValue.max} onChange={e => handleChangeMaxValue(e, surfaceLocalValue.min)} onBlur={handleSurfaceMaxChange} maxLength={6} />
          <span sx={rangeStyles.unit}>m²</span>
        </div>
        {shouldRenderSlider && <div data-cy="InteriorDesignsSurfaceRangeSlider" sx={rangeStyles.sliderWrapper}>
            {/* @ts-expect-error - react-range Direction type issue */}
            <Range min={surfaceRange.min} max={surfaceRange.max} step={1} values={[+surfaceCurrentRange.min, +surfaceCurrentRange.max]} onChange={handleSurfaceSliderChange} renderTrack={renderSurfaceTrack} renderThumb={renderSurfaceThumb} draggableTrack={false} disabled={false} rtl={false} allowOverlap={false} />
          </div>}
      </div>;
  }, [activeTypeFilters, surfaceRange, surfaceCurrentRange, surfaceLocalValue, errorMinValue, errorMaxValue]);
  const renderFilterDefault = (type: IInteriorDesignFilter['type']) => {
    if (!localeFilters) return null;
    return <ul data-cy="InteriorDesignsFilters__links" sx={styles.filterLinkList(true)}>
        {localeFilters.filter(filter => filter.type === type && filter.type !== 'surface').map(filter => {
        if (filter) return <li key={filter.value} data-cy="FiltersLink">
                  {renderFilterLink(filter)}
                </li>;
        return null;
      })}
      </ul>;
  };
  const renderFilter = (type: IInteriorDesignFilter['type']) => {
    if (activeTypeFilters && activeTypeFilters !== type) return null;
    const getRenderfunction = () => type === 'surface' ? renderFilterTypeSurface : renderFilterDefault(type);
    return <li key={type} data-cy="FiltersLink">
        {localeFilters && activeTypeFilters === type ? getRenderfunction() : renderFiltersTypeLink(type)}
      </li>;
  };
  const renderFilters = useMemo(() => {
    return <ul sx={styles.filterLinkList(true)}>
        {renderFilter('furniture')}
        {renderFilter('door')}
        {renderFilter('wall')}
        {renderFilter('floor')}
        {renderFilter('surface')}
      </ul>;
  }, [activeTypeFilters, localeFilters, surfaceLocalValue, surfaceCurrentRange]);
  const clearFilters = () => {
    if (filters) {
      setSurfaceCurrentRange({
        ...surfaceRange
      });
      const newFiltersState = [...filters.map(filter => ({
        ...filter,
        isChecked: false
      }))];
      setLocaleFilters(newFiltersState);
      setActiveTypeFilters(null);
    }
  };
  const handleAsideClose = useCallback(() => {
    if (isMobile) {
      const filterButton = document.querySelector('.filters-button');
      if (filterButton) {
        setTimeout(() => {
          filterButton.scrollIntoView({
            behavior: 'smooth'
          });
        }, 100);
      }
    }
    if (localeFiltersRef.current) setFiltersState([...localeFiltersRef.current]);
    setShowFilters(false);
    setActiveTypeFilters(null);
  }, [isMobile, setFiltersState, setShowFilters]);
  return <ErrorBoundary>
      <Aside data-cy="InteriorDesignsFiltersAside" active={asideActive} onCloseAside={handleAsideClose}>
        <div sx={styles.filterAsideContainer}>
          <div sx={styles.filterHeaderContainer} data-cy="InteriorDesignsFiltersContainerTitle">
            {activeTypeFilters && <Button addSx={styles.backButton} variant="iconOnly" onClick={() => setActiveTypeFilters(null)} cy="InteriorDesignsFiltersContainerBackButton">
                <Icon size={['16px', '16px', '16px', '16px', '16px', '21px']}>
                  <ArrowBack />
                </Icon>
              </Button>}
            <div sx={styles.headerContainer}>
              <Heading h={3} headingType={4} bold align="center">
                {activeTypeFilters ? tCommon(activeTypeFilters.toLowerCase()) : t('filters')}
              </Heading>
            </div>
          </div>
          <div sx={styles.filterContent}>{renderFilters}</div>

          <div sx={styles.filler} />

          <div sx={styles.filterButtons}>
            <CategoriesFilterPanel position="vertical" productsCount={0} leftButtonText={t('resetFilters')} rightButtonText={t('ready')} handleLeftButtonClick={clearFilters} handleRightButtonClick={handleAsideClose} />
          </div>
        </div>
      </Aside>
    </ErrorBoundary>;
};
export default observer(InteriorDesignsFiltersAside);